import { type ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type { VpError } from '../../../generated/internal.graphql';
import * as VPError from '../../../VPError';
import { type InternalApiError, toApiErrors } from '../errors';
import type { VPResult } from '../../VPResult';
import { COLLECTION_METADATA_FIELDS, type CollectionArguments } from '../../collection/Api';
import * as GqlCollection from '../../collection/GqlCollection';
import { CONNECTION_METADATA_FIELDS } from '../../connection/Api';
import * as AccountFeature from './AccountFeature';
import * as AccountOption from './AccountOption';
import * as Client from './Client';
import * as FeeStructure from './FeeStructure';
import * as SoundcheckLoan from './SoundcheckLoan';
import {
  INTERNAL_ACCOUNT_DETAIL_FIELDS,
  INTERNAL_ACCOUNT_LIST_FIELDS,
  INTERNAL_SOUNDCHECK_LOAN_FIELDS,
} from './fragments';
import { ACCOUNT_AGNOSTIC, forAccount } from '../../accountContext';

/**
 * Platform account administration for VenuePilot staff. These live on the internal (VP staff)
 * schema rather than AppSchema (ADR-009): they read and write *any* account regardless of tenancy,
 * which is exactly what should not sit in a published schema. Auth is the superadmin Rails session
 * cookie — a bearer token never satisfies the gate, and it only works in the Rails-embedded admin
 * against a same-origin `/graphql`, never the standalone dev server — so everything here goes
 * through `internalGqlApiClient`.
 *
 * A non-superadmin caller is rejected with HTTP 401 before the query is parsed, which Apollo
 * *throws* rather than returning a payload. Every function below therefore catches and classifies
 * via `toApiErrors`, so callers can branch on `isUnauthorized` and say "VenuePilot staff only"
 * instead of rendering an empty table — and so an expected authorization outcome does not reach
 * Sentry as an exception. **Nothing here throws.**
 *
 * Two result shapes, matching `Internal/Order/Api.ts`: reads resolve `VPResult`, mutations resolve
 * `MutationResult`. The two here that create or update something — `saveAccount` and
 * `createSoundcheckLoan` — carry their entity in the payload and so need no follow-up refetch;
 * `deleteAccount` carries only `success` and `errors`, like the order interventions.
 *
 * The two `*Option` reads are the account switcher's, not the administration screens': they select
 * only what a picker renders. See `AccountOption.ts` for why they do not go through `Client`.
 *
 * ## The account these operations run in
 *
 * Every operation here that names a single account also *sends* it, as `X-VP-Account-Id`, via
 * `forAccount` — `fetchAccount`, `fetchAccountFeatures`, `fetchAccountOption`, `saveAccount` (on
 * update), `deleteAccount` and `createSoundcheckLoan`. The account comes from the argument the
 * caller already passes, so there is nothing for a call site to remember.
 *
 * It has to be sent explicitly because the staff operator is not a member of the account they are
 * administering: the connection store's account is their own, and `InternalGraphqlController`
 * resolves `current_account` from this header. Without it, `saveAccount` on someone else's client
 * was answered in the operator's account — which is how a client logo ended up on the wrong
 * account and the save came back HTTP 200 with a null account (sc-19910).
 *
 * The rest name no single account and say so with `ACCOUNT_AGNOSTIC` — `fetchAccounts`,
 * `fetchAccountOptions` and `fetchFeeStructures` — because the internal chain does not require an
 * account and the operator running them may belong to none. `saveAccount` on *create* is the one
 * operation in this module that still falls back to the store: there is no account until it exists.
 *
 * Every `Internal/*` module now declares itself the same way, most of them cross-account;
 * `accountScope.spec.ts` is where the classification is pinned, so an operation added later cannot
 * inherit whichever account the store happens to hold (sc-20087).
 */

export type MutationResult = { success: boolean; errors: InternalApiError[] };

const INTERNAL_ACCOUNTS_QUERY = gql`
  query InternalAccounts($pagination: PaginationArgument, $filter: AccountFilterArguments) {
    accounts(pagination: $pagination, filter: $filter) {
      collection {
        ...InternalAccountListFields
      }
      metadata {
        ...CollectionMetadataFields
      }
    }
  }
  ${INTERNAL_ACCOUNT_LIST_FIELDS}
  ${COLLECTION_METADATA_FIELDS}
`;

const INTERNAL_ACCOUNT_QUERY = gql`
  query InternalAccount($id: Int!) {
    account(id: $id) {
      ...InternalAccountDetailFields
    }
  }
  ${INTERNAL_ACCOUNT_DETAIL_FIELDS}
`;

// `Int!` although the schema argument is nullable: sent without one, it reads every account's flags.
const INTERNAL_ACCOUNT_FEATURES_QUERY = gql`
  query InternalAccountFeatures($accountId: Int!, $first: Int, $after: String) {
    accountFeatures(accountId: $accountId, first: $first, after: $after) {
      nodes {
        accountId
        feature
      }
      pageInfo {
        ...ConnectionMetadataFields
      }
    }
  }
  ${CONNECTION_METADATA_FIELDS}
`;

const INTERNAL_ACCOUNT_OPTIONS_QUERY = gql`
  query InternalAccountOptions($pagination: PaginationArgument, $filter: AccountFilterArguments) {
    accounts(pagination: $pagination, filter: $filter) {
      collection {
        id
        name
      }
      metadata {
        ...CollectionMetadataFields
      }
    }
  }
  ${COLLECTION_METADATA_FIELDS}
`;

const INTERNAL_ACCOUNT_OPTION_QUERY = gql`
  query InternalAccountOption($id: Int!) {
    account(id: $id) {
      id
      name
    }
  }
`;

const INTERNAL_FEE_STRUCTURES_QUERY = gql`
  query InternalFeeStructures {
    feeStructures {
      id
      name
    }
  }
`;

const INTERNAL_SAVE_ACCOUNT_MUTATION = gql`
  mutation InternalSaveAccount($accountAttributes: AccountAttributes!, $sourceAccountId: Int) {
    saveAccount(input: { accountAttributes: $accountAttributes, sourceAccountId: $sourceAccountId }) {
      account {
        ...InternalAccountDetailFields
      }
      errors {
        message
        path
      }
    }
  }
  ${INTERNAL_ACCOUNT_DETAIL_FIELDS}
`;

const INTERNAL_CREATE_SOUNDCHECK_LOAN_MUTATION = gql`
  mutation InternalCreateSoundcheckLoan(
    $accountId: Int!
    $originalPrincipalBalance: Float!
    $perTicketFeePercentage: Float!
  ) {
    createSoundcheckLoan(input: {
      accountId: $accountId
      originalPrincipalBalance: $originalPrincipalBalance
      perTicketFeePercentage: $perTicketFeePercentage
    }) {
      loan {
        ...InternalSoundcheckLoanFields
      }
      errors {
        message
        path
      }
    }
  }
  ${INTERNAL_SOUNDCHECK_LOAN_FIELDS}
`;

const INTERNAL_DELETE_ACCOUNT_MUTATION = gql`
  mutation InternalDeleteAccount($accountId: Int!) {
    deleteAccount(input: { accountId: $accountId }) {
      success
      errors {
        message
        path
      }
    }
  }
`;

const toErrors = (errors?: VpError[] | null): InternalApiError[] =>
  VPError.fromGqlCollection(errors ?? []);

/**
 * A failed mutation with an empty `errors` list leaves the caller nothing to render. Today's
 * resolvers always populate one — but a null payload does not, and neither would a future
 * `success: false` with no message, so fall back rather than report a silent failure.
 */
const failed = (errors: InternalApiError[], fallback: string): MutationResult => ({
  success: false,
  errors: errors.length ? errors : [{ path: '', message: fallback }],
});

export const fetchAccounts = async (
  client: ApolloClient<NormalizedCacheObject>,
  { pagination, filter }: { pagination?: CollectionArguments; filter?: Client.ClientFilter } = {},
): Promise<VPResult<GqlCollection.GqlCollection<Client.ClientType>, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_ACCOUNTS_QUERY,
      fetchPolicy: 'network-only',
      // Account-agnostic: a platform-wide list has no single account to run in.
      context: ACCOUNT_AGNOSTIC,
      variables: {
        pagination: { page: pagination?.page, perPage: pagination?.limit },
        filter: filter ?? {},
      },
    });

    // `accounts` is nullable. An absent collection is a backend fault, not an empty page — resolving
    // it as one would render "no accounts found" and hide the failure.
    if (!data?.accounts) {
      return { success: false, errors: [{ path: '', message: 'Failed to fetch client accounts.' }] };
    }

    return { success: true, data: GqlCollection.fromGql(data.accounts, Client.fromGqlCollection) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

export const fetchAccount = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<VPResult<Client.ClientType | undefined, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_ACCOUNT_QUERY,
      fetchPolicy: 'network-only',
      variables: { id },
      context: forAccount(id),
    });

    // A missing account is an answer (bad id), not a failure — don't dress it up as one.
    return { success: true, data: data?.account ? Client.fromGql(data.account) : undefined };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

const FEATURES_PAGE_SIZE = 100;
const FEATURES_MAX_PAGES = 20;

export const fetchAccountFeatures = async (
  client: ApolloClient<NormalizedCacheObject>,
  accountId: number,
): Promise<VPResult<AccountFeature.AccountFeature[], InternalApiError[]>> => {
  try {
    const features: AccountFeature.AccountFeature[] = [];
    let after: string | undefined;
    let cursor: string | undefined;
    let pages = 0;

    // An internal connection may cap a page below `first` (EmailDeliverability's does), so read to the end.
    do {
      after = cursor;
      pages += 1;
      const { data } = await client.query({
        query: INTERNAL_ACCOUNT_FEATURES_QUERY,
        fetchPolicy: 'network-only',
        variables: { accountId, first: FEATURES_PAGE_SIZE, after },
        context: forAccount(accountId),
      });

      const connection = data?.accountFeatures;
      if (!connection) {
        return { success: false, errors: [{ path: '', message: 'Failed to fetch account features.' }] };
      }

      features.push(...(connection.nodes ?? []).filter(Boolean).map(AccountFeature.fromGql));
      cursor = connection.pageInfo.hasNextPage ? connection.pageInfo.endCursor ?? undefined : undefined;
    } while (cursor && cursor !== after && pages < FEATURES_MAX_PAGES);

    // Only this account's own rows: `feature_enabled?` never reads the null-account defaults.
    return { success: true, data: features.filter(feature => feature.accountId === accountId) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

/**
 * The account switcher's list. Paged and filtered server-side because a superadmin's list is the
 * whole platform — client-side filtering over a fetched-in-full list stops being usable well before
 * that scale.
 */
export const fetchAccountOptions = async (
  client: ApolloClient<NormalizedCacheObject>,
  { pagination, filter }: { pagination?: CollectionArguments; filter?: Client.ClientFilter } = {},
): Promise<VPResult<GqlCollection.GqlCollection<AccountOption.AccountOption>, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_ACCOUNT_OPTIONS_QUERY,
      fetchPolicy: 'network-only',
      // Account-agnostic: as `fetchAccounts`, and the switcher asks before an account is chosen.
      context: ACCOUNT_AGNOSTIC,
      variables: {
        pagination: { page: pagination?.page, perPage: pagination?.limit },
        filter: filter ?? {},
      },
    });

    // Same reasoning as `fetchAccounts`: an absent collection is a backend fault, not an empty page.
    // Resolving it as one would leave the switcher rendering an empty list over a failed read.
    if (!data?.accounts) {
      return { success: false, errors: [{ path: '', message: 'Failed to fetch accounts.' }] };
    }

    return { success: true, data: GqlCollection.fromGql(data.accounts, AccountOption.fromGqlCollection) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

/**
 * One account by id, for the switcher's *current* selection. An impersonated account routinely sits
 * outside the first page of a platform-wide list, and the picker cannot label a selection it has
 * never seen.
 */
export const fetchAccountOption = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<VPResult<AccountOption.AccountOption | undefined, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_ACCOUNT_OPTION_QUERY,
      fetchPolicy: 'network-only',
      variables: { id },
      context: forAccount(id),
    });

    // A missing account is an answer (stale id), not a failure — don't dress it up as one.
    return { success: true, data: data?.account ? AccountOption.fromGql(data.account) : undefined };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

export const fetchFeeStructures = async (
  client: ApolloClient<NormalizedCacheObject>,
): Promise<VPResult<FeeStructure.FeeStructure[], InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_FEE_STRUCTURES_QUERY,
      fetchPolicy: 'network-only',
      // Account-agnostic: the fee-structure catalog belongs to the platform.
      context: ACCOUNT_AGNOSTIC,
    });

    return { success: true, data: (data?.feeStructures ?? []).map(FeeStructure.fromGql) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

export const saveAccount = async (
  client: ApolloClient<NormalizedCacheObject>,
  accountAttributes: Client.ClientInput,
  sourceAccountId?: number,
): Promise<MutationResult & { account?: Client.ClientType }> => {
  try {
    const { data } = await client.mutate({
      mutation: INTERNAL_SAVE_ACCOUNT_MUTATION,
      variables: { accountAttributes, sourceAccountId },
      fetchPolicy: 'network-only',
      context: accountAttributes.id ? forAccount(accountAttributes.id) : undefined,
    });

    const errors = toErrors(data?.saveAccount?.errors);
    const account = data?.saveAccount?.account;

    if (errors.length || !account) {
      return failed(errors, 'Failed to save the account.');
    }

    return { success: true, errors: [], account: Client.fromGql(account) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

export const createSoundcheckLoan = async (
  client: ApolloClient<NormalizedCacheObject>,
  input: { accountId: number; originalPrincipalBalance: number; perTicketFeePercentage: number },
): Promise<MutationResult & { loan?: SoundcheckLoan.Loan }> => {
  try {
    const { data } = await client.mutate({
      mutation: INTERNAL_CREATE_SOUNDCHECK_LOAN_MUTATION,
      variables: input,
      fetchPolicy: 'network-only',
      context: forAccount(input.accountId),
    });

    const errors = toErrors(data?.createSoundcheckLoan?.errors);
    const loan = data?.createSoundcheckLoan?.loan;

    if (errors.length || !loan) {
      return failed(errors, 'Failed to create the Soundcheck loan.');
    }

    return { success: true, errors: [], loan: SoundcheckLoan.fromGql(loan) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

export const deleteAccount = async (
  client: ApolloClient<NormalizedCacheObject>,
  accountId: number,
): Promise<MutationResult> => {
  try {
    const { data } = await client.mutate({
      mutation: INTERNAL_DELETE_ACCOUNT_MUTATION,
      variables: { accountId },
      fetchPolicy: 'network-only',
      context: forAccount(accountId),
    });

    if (!data?.deleteAccount?.success) {
      return failed(toErrors(data?.deleteAccount?.errors), 'Failed to archive the account.');
    }

    return { success: true, errors: [] };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};
